/**
 * Base URL for the Hera API (`VITE_HERA_API_URL`).
 *
 * Local FastAPI/uvicorn is almost always **HTTP**. If `VITE_HERA_API_URL` is set to
 * `https://localhost` / `https://127.0.0.1`, the browser will try TLS and fail with
 * `net::ERR_ALPN_NEGOTIATION_FAILED`. In dev we normalize to `http` unless opted out.
 */
export function getHeraApiBaseUrl(): string {
  const raw = import.meta.env.VITE_HERA_API_URL ?? "";
  const allowHttpsLocal =
    import.meta.env.VITE_HERA_API_ALLOW_HTTPS_LOCAL === "true";

  if (!import.meta.env.DEV || allowHttpsLocal || !raw.trim()) {
    return raw;
  }

  try {
    const u = new URL(raw);
    if (
      u.protocol === "https:" &&
      (u.hostname === "localhost" || u.hostname === "127.0.0.1")
    ) {
      const httpUrl = `http://${u.host}${u.pathname}${u.search}${u.hash}`;
      console.warn(
        "[Hera] VITE_HERA_API_URL uses https:// for localhost; local API is normally plain HTTP (see backend README). Using http:// instead. Fix .env to VITE_HERA_API_URL=http://localhost:8000 or set VITE_HERA_API_ALLOW_HTTPS_LOCAL=true if you terminate TLS locally.",
        { was: raw, using: httpUrl },
      );
      return httpUrl;
    }
  } catch {
    // leave raw as-is if not a valid URL
  }

  return raw;
}

/**
 * Authorize parameter read by the Auth0 post-login action that is deployed today.
 * Localhost values make that action skip the webhook; the API then creates the
 * operator login identity itself. The action source in this repo ignores the
 * parameter once that version is deployed.
 */
export function auth0LoginWebhookUrl(): string {
  return `${getHeraApiBaseUrl().replace(/\/$/, "")}/webhooks/auth0/on_login`;
}
